// @flow
import { array, bool, mixed, number, object, string } from 'yup';
import { getDiscountRangeFuncForMetric } from '@components/custom-discounts/mld-for-field/utils';

import {
  CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_BACKEND_NAME,
  CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
  DEFAULT_VALUE_FOR_DB_ROW_ID,
  GD_MIN_ACV_FOR_NON_ADMIN,
  GD_MIN_DISCOUNT_FOR_NON_ADMIN,
  GLOBAL_DISCOUNT_BACKEND_NAME,
  IS_ROW_FROZEN,
  IS_VBD,
  METRICS_LEVEL_DISCOUNTS_CONFIG_ARRAY_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_VALUES_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DISCOUNT_BACKEND_NAME,
  METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME,
  NATIVE_INFERENCE_MLD_MAX_DISCOUNT_FOR_ADMIN_PERCENT,
  NATIVE_INFERENCE_MLD_METRIC_NAMES,
  RATE_CARD_BACKEND_NAME,
  VBD_SUPPORTING_RCS,
} from '../constants';
import { TEXT_NUMBER_INPUT_TYPE } from '../formik-utils/consts';
import {
  doDimensionValuesConflict,
  formatMLDIntoFrontEndFormat,
  getAvgACVAtListPrice,
  getGDRangesFromEstimate,
  getMaxGDForNonAdmin,
  getIfGivenMLDRowIsOpenedForEditing,
  getMetricNameFromMetricRow,
  getCurrentMLDRowDimensionValues,
  extractDimRelatedKeysFromGivenRowValues,
  extractDimValuesFromRow,
} from '../components/custom-discounts/utils';
import {
  areAllValuesInGivenArrayNonNull,
  getIfAllTheGivenKeysInTheGivenObjectAreNonNull,
} from '../common-utils/utils';
import { formatFloat1DP } from '../components/views/grid-utils/renderers';

import { convertConfigArrayToMap } from './utils';
import { discountValidation } from './estimate-commit-details';

export const getIfVBDIsSupportedForRC = (formikValues) => {
  const currentRateCard = formikValues[RATE_CARD_BACKEND_NAME];
  return VBD_SUPPORTING_RCS.includes(currentRateCard);
};

export const allDimensionsFilledTest = (data, { createError, path, from }) => {
  const currRow = from[0].value;

  // Only validate rows that are being edited or newly added
  if (currRow?.isRowFrozen === true || currRow?.isRowOpenedForEditing === false) {
    return true;
  }

  // Extract only dimension-related field names.
  const dimRelatedKeys = extractDimRelatedKeysFromGivenRowValues(currRow);

  const allDimensionsFilled = getIfAllTheGivenKeysInTheGivenObjectAreNonNull(
    currRow,
    dimRelatedKeys
  );

  if (!allDimensionsFilled) {
    return createError({
      message: `Please fill all dimension fields before updating discounts!`,
      path: path,
    });
  }

  return true;
};

// MLD discount guidance validation - checks discount is within allowed range
// Admin users: allowed any discount (0-100%), skip guidance validation
// Non-admin users: must be within guidance range, prompted to contact DSM if exceeded
export const mldDiscountValidationBasedOnGuidanceTest =
  (mldGuidance, isUserAdmin) =>
  (data, { createError, path, from }) => {
    const currRow = from[0].value;

    // Skip for rows which the user is not editing
    if (!getIfGivenMLDRowIsOpenedForEditing(currRow)) {
      return true;
    }

    // Admin users can apply any discount (0-100%) and this check is already covered
    if (isUserAdmin) {
      return true;
    }

    // Get metric name from parent
    const parentInputs = from[1].value;
    const metricName = getMetricNameFromMetricRow(parentInputs);

    // Get discount range for current row using config-based approach
    const dimensionValues = getCurrentMLDRowDimensionValues(currRow);
    const discountValue = Number(data);

    const getDiscountRangeFunc = getDiscountRangeFuncForMetric(metricName);
    const discountRangeResult = getDiscountRangeFunc(dimensionValues, mldGuidance, metricName);

    // No guidance for this dimension combination, non-admins cannot edit
    if (!discountRangeResult) {
      return createError({
        message:
          'Max permitted discount is 0%. Please contact Deal Strategy for discount is needed',
        path: path,
      });
    }

    const { minDiscount, maxDiscount, connectorToDiscountListString } = discountRangeResult;

    // Validate within range - use connector-specific message if connectorToDiscountListString exists
    if (discountValue > maxDiscount) {
      if (connectorToDiscountListString) {
        return createError({
          message: `Max permitted discount is ${maxDiscount}%. \n\nDiscount exceeds the allowed maximum for at least one of the connectors selected. \nWith multiple connector types selected, the discount is capped at the lowest allowed maximum.\n\nConnector discount limits:\n${connectorToDiscountListString}\n\nSplit connectors into individual selections to apply higher discounts where allowed.`,
          path: path,
        });
      }
      return createError({
        message: `Max permitted discount is ${maxDiscount}%. Please contact Deal Strategy for higher discounts.`,
        path: path,
      });
    }

    if (discountValue < minDiscount) {
      return createError({
        message: `Min permitted discount is ${minDiscount}%.`,
        path: path,
      });
    }

    return true;
  };

// NI (Native Inference) metrics are capped at a fixed max discount, even for admins.
export const getNIAdminMaxDiscountTestFunc =
  (isUserAdmin) =>
  (data, { createError, path, from }) => {
    const currRow = from[0].value;

    // Skip for rows which the user is not editing
    if (!getIfGivenMLDRowIsOpenedForEditing(currRow)) {
      return true;
    }

    // Non-admins are already bound by the guidance test above
    if (!isUserAdmin) {
      return true;
    }

    const parentInputs = from[1].value;
    const metricName = getMetricNameFromMetricRow(parentInputs);

    if (!NATIVE_INFERENCE_MLD_METRIC_NAMES.includes(metricName)) {
      return true;
    }

    const discountValue = Number(data);
    if (discountValue > NATIVE_INFERENCE_MLD_MAX_DISCOUNT_FOR_ADMIN_PERCENT) {
      return createError({
        message: `Max permitted discount for this metric is ${NATIVE_INFERENCE_MLD_MAX_DISCOUNT_FOR_ADMIN_PERCENT}%, even for admins.`,
        path: path,
      });
    }

    return true;
  };

export const getIfCurrentVBDConflictsWithAnyMLD = (
  completeDimensionsArray,
  rowValues,
  isVBDRow
) => {
  // If it is not a VBD row, simply return
  if (!isVBDRow) {
    return { doesVBDRowConflictWithAnyOtherUserRow: false, vbdRowConflictingWithUserRowNumber: -1 };
  }

  let conflictingRowNum = -1;
  for (let rowNumber = 0; rowNumber <= completeDimensionsArray.length - 1; rowNumber++) {
    // Don't compare with newly added rows
    if (completeDimensionsArray[rowNumber].id === DEFAULT_VALUE_FOR_DB_ROW_ID) {
      continue;
    }
    const dimRelatedKeys = extractDimRelatedKeysFromGivenRowValues(rowValues);
    const currRowDimValues = extractDimValuesFromRow(rowValues, dimRelatedKeys);
    const parentRowBeingConsidered = completeDimensionsArray[rowNumber];
    if (parentRowBeingConsidered?.[IS_VBD] === true) {
      continue; // dont check conflicts with VBD rows
    }
    const parentRowDimValues = extractDimValuesFromRow(parentRowBeingConsidered, dimRelatedKeys);
    if (doDimensionValuesConflict(parentRowDimValues, currRowDimValues)) {
      conflictingRowNum = rowNumber;
      break;
    }
  }

  const doesVBDRowConflictWithAnyOtherUserRow = conflictingRowNum !== -1;
  const vbdRowConflictingWithUserRowNumber = conflictingRowNum;
  return { doesVBDRowConflictWithAnyOtherUserRow, vbdRowConflictingWithUserRowNumber };
};

// Note, this should NOT be converted to arrow function
export const conflictsTest = (data, { createError, path, from }) => {
  const currRow = from[0].value;

  // If it is not a row being edited or a row being added, we don't have to test this one
  if (currRow?.isRowFrozen === true || currRow?.isRowOpenedForEditing === false) {
    return true;
  }

  const parentInputs = from[1].value;
  const allRows = parentInputs[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME];

  const inProgressRow = allRows.find((x) => x[IS_ROW_FROZEN] === false);

  if (!inProgressRow) {
    return true;
  }

  const inProgressRowDimensionValues = getCurrentMLDRowDimensionValues(inProgressRow);
  const inProgressRowDimValuesAlongWithDiscount = [
    ...inProgressRowDimensionValues,
    inProgressRow[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DISCOUNT_BACKEND_NAME],
  ];

  let conflictingRowNum = null;

  // If there are more than 1 MLD items defined and all the Dimension values && Discount is filled, then perform the check
  if (
    allRows.length > 1 &&
    areAllValuesInGivenArrayNonNull(inProgressRowDimValuesAlongWithDiscount)
  ) {
    // Then check if the last row value conflicts with any other row
    for (let rowNumber = 0; rowNumber <= allRows.length - 2; rowNumber++) {
      const currRowDimValues = getCurrentMLDRowDimensionValues(allRows[rowNumber]);
      // For CE1, we are implementing NS solution, so, MLD and VBD rows both should be put together for conflict checking
      // For CE2, we are NOT implementing NS solution, so, VBD rows should not be considered for conflict checks
      // Hence, this temporary change
      if (allRows?.[rowNumber]?.metric_discount_id?.startsWith('CVBD') === true) {
        continue; // dont check conflicts with VBD rows
      }

      if (doDimensionValuesConflict(inProgressRowDimensionValues, currRowDimValues)) {
        conflictingRowNum = rowNumber;
        break;
      }
    }
  }

  if (conflictingRowNum != null) {
    return createError({
      message: `This row conflicts with row ${conflictingRowNum + 1} , please check!`,
      path: path,
    });
  }

  return true;
};

// Helper to create MLD schema with guidance validation
const getMldDiscountSchema = (mldGuidance, isUserAdmin) =>
  array(
    object({
      [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME]: string()
        .required()
        .label('Metric Name')
        .nullable()
        .typeError('Please select a metric!')
        .test(
          'notAllDimensionConfigItemsFrozen',
          'Please ensure all Dimension Config Items are frozen except possibly the last one',
          function () {
            return this.parent[METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME].slice(
              0,
              -1
            ).every((dimensionConfigItem) => dimensionConfigItem[IS_ROW_FROZEN] === true);
          }
        ),
      [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME]: array(
        object({
          [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME]: string().required(),
          [IS_ROW_FROZEN]: bool().required(),
          [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DIMENSION_VALUES_BACKEND_NAME]: array().of(
            mixed().test(
              'string-or-non-empty-array',
              'Must be a non-empty string or non-empty array',
              (value) => {
                if (typeof value === 'string') {
                  return value.length > 0; // non-empty string
                }
                if (Array.isArray(value)) {
                  return value.length > 0; // non-empty array
                }
                return false; // neither string nor array
              }
            )
          ),
          [METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_DISCOUNT_BACKEND_NAME]: number()
            .nullable()
            .typeError('Discount is a required field!')
            .label('Discount')
            .min(0)
            .max(100)
            .required()
            .test('allDimensionsFilled', 'Please fill all dimensions', allDimensionsFilledTest)
            .test('conflictsExist', 'Please ensure there are NO conflicts', conflictsTest)
            .test(
              'mldDiscountGuidance',
              'Discount must be within allowed range',
              mldDiscountValidationBasedOnGuidanceTest(mldGuidance, isUserAdmin)
            )
            .test(
              'niAdminMaxDiscount',
              `Discount must not exceed ${NATIVE_INFERENCE_MLD_MAX_DISCOUNT_FOR_ADMIN_PERCENT}% for Native Inference metrics`,
              getNIAdminMaxDiscountTestFunc(isUserAdmin)
            ),
        })
      ).min(1),
    })
  )
    .notRequired()
    .default([]);

// GD automation validation with MLD guidance
export const customDiscountsValidationSchemaFunc = (estimate) => {
  const isUserAdmin = estimate?.isUserAdmin;
  const dealYears = Number(estimate?.deal_duration?.deal_duration_years ?? 1);
  const ranges = getGDRangesFromEstimate(estimate);
  const avgACVAtList = getAvgACVAtListPrice(estimate);
  const initialGDValue = estimate?.inputs?.global_discount;
  const maxDiscountForNonAdmin = ranges?.length
    ? getMaxGDForNonAdmin(avgACVAtList, ranges, dealYears)
    : 100;

  // MLD guidance for discount range validation
  const mldGuidance = estimate?.mldGuidance;

  return object({
    [GLOBAL_DISCOUNT_BACKEND_NAME]: discountValidation
      .notRequired()
      .test(
        'gdAdminNonAdminValidation',
        'Global discount must be within the allowed range for your role',
        function (value) {
          const currentValue = Number(value);
          const initialValueNum = Number(initialGDValue ?? 0);
          const hasValueChanged = currentValue !== initialValueNum;

          // Skip if: viewing (no change), admin
          if (!hasValueChanged || isUserAdmin) {
            return true;
          }

          // Non-admin editing - apply tier-based limits
          // Average ACV >= $21,052 qualifies for 5% min discount tier
          const minDiscount =
            avgACVAtList >= GD_MIN_ACV_FOR_NON_ADMIN ? GD_MIN_DISCOUNT_FOR_NON_ADMIN : 0;

          if (currentValue > maxDiscountForNonAdmin) {
            const errorMsg =
              maxDiscountForNonAdmin === 0
                ? 'Max pre-approved discount is 0%. Reach out to DSM if higher discount is needed.'
                : `Up to ${formatFloat1DP(
                    maxDiscountForNonAdmin
                  )}% discount is permitted. For higher discounts, please reach out to DSM.`;
            return this.createError({ message: errorMsg });
          }

          if (currentValue < minDiscount) {
            return this.createError({
              message: `Minimum ${formatFloat1DP(
                minDiscount
              )}% discount is required. For lower discounts, please reach out to DSM.`,
            });
          }

          return true;
        }
      ),
    [METRICS_LEVEL_DISCOUNTS_CONFIG_ARRAY_BACKEND_NAME]: getMldDiscountSchema(
      mldGuidance,
      isUserAdmin
    ),
  });
};

export const CUSTOM_DISCOUNTS_JSON_CONFIG = {
  backendFieldName: CUSTOM_DISCOUNTS_JSON_BACKEND_NAME,
  inputTransformationFunc: (metricsDiscounts) => {
    return {
      [GLOBAL_DISCOUNT_BACKEND_NAME]: Number(metricsDiscounts?.global_discount ?? 0.0),
      [METRICS_LEVEL_DISCOUNTS_CONFIG_ARRAY_BACKEND_NAME]: formatMLDIntoFrontEndFormat(
        metricsDiscounts?.metrics ?? []
      ),
    };
  },
  validation: customDiscountsValidationSchemaFunc,
};

export const METRICS_LEVEL_DISCOUNTS_TOP_LEVEL_CONFIG_ARRAY_CONFIG = [
  {
    backendFieldName: METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_METRIC_NAME_BACKEND_NAME,
    hidden: false,
    displayName: 'Metric',
    columnWidth: 3,
    colDefaultValue: null,
  },
  {
    backendFieldName: METRICS_LEVEL_DISCOUNTS_DIMENSIONS_CONFIG_ARRAY_BACKEND_NAME,
    displayName: 'Dimensions Config',
    hidden: false,
    columnWidth: 3,
    colDefaultValue: [],
  },
];

export const METRICS_LEVEL_DISCOUNTS_TOP_LEVEL_CONFIG_ARRAY_CONFIG_MAP = convertConfigArrayToMap(
  METRICS_LEVEL_DISCOUNTS_TOP_LEVEL_CONFIG_ARRAY_CONFIG
);

export const CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_CONFIG = {
  backendFieldName: CUSTOM_DISCOUNTS_GLOBAL_DISCOUNT_BACKEND_NAME,
  displayName: 'Global Discount',
  inputType: TEXT_NUMBER_INPUT_TYPE,
  icon: 'percent',
};
